import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import { persistor } from '../store/store';
import { logout, setCredentials } from '../store/slices/authSlice';
import { authAPI } from '../services/api';
import { isTokenExpired } from '../utils/jwt';

export type AuthStatus = 'authenticated' | 'unauthenticated' | 'refreshing';

// 같은 refreshToken으로 이미 재발급을 시도했는데도 여전히 'refreshing'으로 남는 경우의 허용 한도.
// (예: 클라이언트 시계가 서버보다 빨라 재발급된 accessToken조차 즉시 만료로 판정되는 경우)
// 이 한도를 넘으면 재시도해도 해결되지 않는다고 보고 확실히 unauthenticated로 전이해 무한 스피너를 막는다.
const MAX_STUCK_REFRESH_COUNT = 1;

function resolveStatus(accessToken: string | null, refreshToken: string | null): AuthStatus {
  if (!isTokenExpired(accessToken)) return 'authenticated';
  if (!isTokenExpired(refreshToken)) return 'refreshing';
  return 'unauthenticated';
}

/** 자동 로그아웃 시 서버에 저장된 refreshToken도 best-effort로 삭제한다 (실패해도 클라이언트 로그아웃은 진행). */
function bestEffortServerLogout() {
  authAPI.logout().catch(() => {
    // 서버 로그아웃 실패(네트워크 오류, 이미 만료된 accessToken 등)는 무시 - 클라이언트 로그아웃을 막지 않는다.
  });
}

/**
 * accessToken의 만료(exp) 여부를 직접 확인해, 만료된 토큰으로 "로그인된 척" 렌더링되는 것을 막는 훅.
 * (기존 ProtectedRoute는 토큰 존재 여부만 확인해, 2주 전 만료된 토큰도 로그인 상태로 취급하던 버그가 있었음)
 *
 * - accessToken이 아직 유효 → 'authenticated'
 * - accessToken은 만료됐지만 refreshToken이 유효 → 'refreshing' (조용히 재발급 시도, 성공 시 'authenticated')
 * - 재발급 실패 또는 refreshToken까지 만료/부재 → 'unauthenticated' + 남은 토큰 즉시 정리
 */
export function useAuthStatus(): AuthStatus {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const { accessToken, refreshToken } = useSelector((state: RootState) => state.auth.tokens);

  const [status, setStatus] = useState<AuthStatus>(() => resolveStatus(accessToken, refreshToken));
  // 동일한 refreshToken으로 중복 재발급 요청을 보내지 않기 위한 가드 (StrictMode 이중 호출 포함)
  const attemptedRefreshTokenRef = useRef<string | null>(null);
  // 재발급을 시도한 시점의 accessToken 스냅샷. StrictMode 중복 호출(같은 렌더, accessToken 변화 없음)과
  // 실제 재발급 완료 후에도 여전히 만료로 판정되는 상황(accessToken이 바뀜)을 구분하기 위함
  const attemptedAccessTokenRef = useRef<string | null>(null);
  // 같은 refreshToken으로 이미 재발급을 시도한 뒤에도 accessToken이 바뀌었는데 계속 'refreshing'인 횟수
  const stuckRefreshCountRef = useRef(0);

  useEffect(() => {
    const nextStatus = resolveStatus(accessToken, refreshToken);
    setStatus(nextStatus);

    if (nextStatus === 'unauthenticated') {
      // accessToken/refreshToken 둘 다 만료된 오래된 토큰이 남아있다면 즉시 정리
      if (accessToken || refreshToken) {
        bestEffortServerLogout();
        dispatch(logout());
      }
      return;
    }

    if (nextStatus !== 'refreshing') {
      // refreshing 상태를 벗어났으니(재발급 성공 등) 다음 재발급 사이클을 위해 가드를 모두 초기화.
      // (같은 refreshToken이 만료 전까지 여러 차례 자연스럽게 재발급될 수 있으므로, 매 사이클마다
      //  "이미 시도했었는지" 판단을 새로 할 수 있어야 함 — 초기화하지 않으면 다음 정상 재발급 시도까지
      //  "이미 시도함"으로 오판해 곧바로 로그아웃시키는 회귀가 생긴다)
      attemptedRefreshTokenRef.current = null;
      attemptedAccessTokenRef.current = null;
      stuckRefreshCountRef.current = 0;
      return;
    }

    if (!refreshToken || !user) {
      // refreshToken은 유효한데 user 정보가 없는 비정상 상태 → 안전하게 로그아웃 처리
      bestEffortServerLogout();
      dispatch(logout());
      setStatus('unauthenticated');
      return;
    }

    if (attemptedRefreshTokenRef.current === refreshToken) {
      // accessToken이 실제로 바뀌었는데도(=재발급이 완료됐는데도) 여전히 'refreshing'이라면
      // (재발급된 accessToken조차 클라이언트 기준으로 즉시 만료 판정되는 시계 오차 등) 다시 시도해도
      // 해결되지 않으므로 한도를 넘으면 확실히 unauthenticated로 전이해 무한 스피너를 방지한다.
      // accessToken이 그대로면 StrictMode 중복 호출이거나 이전 시도가 아직 진행 중인 것이므로 대기만 한다.
      if (accessToken !== attemptedAccessTokenRef.current) {
        attemptedAccessTokenRef.current = accessToken;
        stuckRefreshCountRef.current += 1;
        if (stuckRefreshCountRef.current >= MAX_STUCK_REFRESH_COUNT) {
          bestEffortServerLogout();
          dispatch(logout());
          setStatus('unauthenticated');
        }
      }
      return;
    }
    attemptedRefreshTokenRef.current = refreshToken;
    attemptedAccessTokenRef.current = accessToken;
    stuckRefreshCountRef.current = 0;

    let cancelled = false;

    (async () => {
      try {
        const { accessToken: newAccessToken } = await authAPI.refreshToken({ refreshToken });
        if (cancelled) return;

        dispatch(setCredentials({ user, accessToken: newAccessToken, refreshToken }));
        // Redux Persist가 localStorage에 저장 완료될 때까지 대기 (레이스 컨디션 방지)
        await persistor.flush();
      } catch {
        // 재발급 실패(리프레시 토큰도 무효) → 로그아웃 처리, 조용히 삼키지 않고 상태를 명확히 전이
        if (cancelled) return;
        bestEffortServerLogout();
        dispatch(logout());
        setStatus('unauthenticated');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessToken, refreshToken, user, dispatch]);

  return status;
}
